/** Resolving each grid row to the date it will be filed on.
 *
 *  Its own file, with no imports, because the grid itself reaches the auth context
 *  and the router - so a test that imported `NewTasks.tsx` to reach one pure function
 *  would need a browser. The rule is worth testing; the page is not what is being
 *  tested.
 */
/** What to STORE when somebody picks a date on a row.
 *
 *  Blank when it matches the day at the top, and the blank is what makes the column
 *  predictable. The cell displays the EFFECTIVE date, so a row inheriting the page
 *  date and a row explicitly set to the same date look identical - and without this
 *  they would behave differently, one following a change to the page date and the
 *  other not, with nothing on screen to tell them apart.
 *
 *  Normalising here means the rule is exactly what somebody would guess from looking
 *  at it: a row showing the day at the top follows it, a row showing a different date
 *  does not. It is also how a row gets back to following - pick the page date again.
 */
export function pickedDate(chosen: string, pageDate: string): string {
  return chosen === pageDate ? '' : chosen
}

/** The rows as the API wants them, with each row's date resolved.
 *
 *  A blank per-row date falls back to the day at the top. That fallback is the whole
 *  design: the page picker stopped being "the date" and became "the default", so a
 *  list of twenty for tomorrow is one edit and the one row that belongs elsewhere
 *  still says so.
 *
 *  Rows with no task are dropped here as they always were - somebody who sets a date
 *  and types nothing has not written a task, and filing an empty row because it had
 *  a date on it would be a new way to create nonsense.
 *
 *  Pure and exported so the rule can be tested without a renderer.
 */
export function datedRows<T extends { task: string; date: string;
                                      end_date?: string }>(
    rows: T[], day: string):
      (Omit<T, 'problems' | 'reference'> & { end_date: string | null })[] {
  return rows.filter((r) => r.task.trim()).map((r) => {
    // `problems` is why an imported cell is empty - a note for the screen, not a
    // field of a task. `reference` is a `File`, which has no JSON form at all and
    // would serialise to `{}`; it is uploaded separately once the task exists. Both
    // are dropped here, which keeps this function's promise: the rows as the API
    // wants them.
    const { problems: _problems, reference: _reference, ...rest } =
      r as T & { problems?: unknown; reference?: unknown }
    return {
      ...rest,
      date: r.date || day,
      // The API takes a date or null. An empty string is neither, and would come
      // back a 422 for every ordinary row in the list.
      end_date: r.end_date || null,
    } as Omit<T, 'problems' | 'reference'> & { end_date: string | null }
  })
}

/** Replace one row with one row per chosen company.
 *
 *  Real rows rather than a hidden list on one row: each keeps **its own** company's
 *  default approver, which a single approver field cannot express, and each stays
 *  editable before anything is filed. It also keeps the cap honest and keeps the
 *  server's "Row 3" refusals pointing at a row somebody can actually see.
 *
 *  `null` rather than a truncated list when it would overflow. Silently dropping the
 *  companies past the cap is the one outcome nobody could detect: the picker said
 *  forty and the grid would show two hundred, with the difference invisible. The
 *  caller refuses and says so.
 *
 *  One tick and ten go through here identically - for a single company this is just
 *  setting it. Treating them as one operation is the point; the old modal made
 *  "several" a separate act.
 *
 *  Order follows `names`, so a fanned-out row comes out in whatever order the picker
 *  offered - which is the firm's own `sort_order` from Master data, not however the
 *  API happened to return them.
 *
 *  Pure and exported so the rule can be tested without a renderer.
 */
export function fanOut<T extends { company: string; approver_ids: string[] }>(
    rows: T[], at: number, names: string[],
    approverFor: (name: string) => string, max: number): T[] | null {
  const src = rows[at]
  if (!src) return null
  if (rows.length - 1 + names.length > max) return null
  const made = names.map((name) => {
    const approver = approverFor(name)
    return { ...src, company: name, approver_ids: approver ? [approver] : [] }
  })
  return [...rows.slice(0, at), ...made, ...rows.slice(at + 1)]
}

/** Fold imported rows into the grid.
 *
 *  Appended, not substituted. Somebody who has typed four rows and then uploads a
 *  sheet has not asked for those four to be thrown away, and there is no undo on this
 *  screen - so the destructive reading of "import" is the wrong default.
 *
 *  The grid's trailing blank rows go first, though. It always keeps one spare to type
 *  into, and after a few `Add row` clicks there may be several; appending under them
 *  would leave a gap of empty rows in the middle of the list.
 *
 *  Returns what was refused as well as the rows, because the cap has to be reported
 *  rather than enforced in silence: a sheet of 300 landing as 200 with nothing said is
 *  a hundred filings nobody knows are missing.
 */
export function mergeImported<T extends { task: string }>(
    rows: T[], imported: T[], max: number,
    blank: () => T): { rows: T[]; refused: number } {
  let kept = [...rows]
  while (kept.length && !kept[kept.length - 1].task.trim()) kept.pop()
  const room = Math.max(0, max - kept.length)
  const taken = imported.slice(0, room)
  const out = [...kept, ...taken]
  // The spare row the grid's own effect would add anyway - done here so the list is
  // never momentarily full to the cap and then one longer.
  if (out.length < max) out.push(blank())
  return { rows: out, refused: imported.length - taken.length }
}
